import React, { useCallback, useEffect, useState } from 'react';
import {
  pePortfolioService,
  type PeHierarchyScopeNodeDto,
  type PeHierarchyScopeNodesBundle,
  type PePortfolioUserScopeDto,
} from '@/services/pePortfolioService';
import { toast } from 'react-hot-toast';

const TYPES = ['BUSINESS', 'FUNCTIONAL', 'GEOGRAPHIC'] as const;

function labelForType(t: (typeof TYPES)[number]): string {
  if (t === 'BUSINESS') return 'Business hierarchy';
  if (t === 'FUNCTIONAL') return 'Functional hierarchy';
  return 'Geographic hierarchy';
}

function indentLabel(node: PeHierarchyScopeNodeDto): string {
  const pad = '· '.repeat(Math.max(0, node.level - 1));
  return `${pad}${node.name}`.trim() || node.name;
}

export interface PePortfolioScopeHierarchyModalProps {
  scope: PePortfolioUserScopeDto | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  /** i18n fallbacks in parent */
  title: string;
  intro: string;
  fullTreeLabel: string;
  saveLabel: string;
  cancelLabel: string;
  successToast: string;
}

export const PePortfolioScopeHierarchyModal: React.FC<PePortfolioScopeHierarchyModalProps> = ({
  scope,
  open,
  onClose,
  onSaved,
  title,
  intro,
  fullTreeLabel,
  saveLabel,
  cancelLabel,
  successToast,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [nodes, setNodes] = useState<PeHierarchyScopeNodesBundle | null>(null);
  const [businessId, setBusinessId] = useState('');
  const [functionalId, setFunctionalId] = useState('');
  const [geographicId, setGeographicId] = useState('');

  const resetFromScope = useCallback((s: PePortfolioUserScopeDto, bundle: PeHierarchyScopeNodesBundle | null) => {
    const roots = s.pe_scope_data_roots;
    const r = roots && typeof roots === 'object' ? roots : null;
    setBusinessId(r?.['BUSINESS']?.rootNodeId ?? '');
    setFunctionalId(r?.['FUNCTIONAL']?.rootNodeId ?? '');
    setGeographicId(r?.['GEOGRAPHIC']?.rootNodeId ?? '');
    if (bundle) {
      setNodes(bundle);
    }
  }, []);

  useEffect(() => {
    if (!open || !scope) {
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const bundle = await pePortfolioService.getChildHierarchyScopeNodes(scope.child_tenant_id);
        if (cancelled) return;
        resetFromScope(scope, bundle);
      } catch (e) {
        if (!cancelled) {
          toast.error(pePortfolioService.errMessage(e));
          setNodes(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, scope, resetFromScope]);

  const onSave = async () => {
    if (!scope) return;
    const roots: Record<string, { rootNodeId: string }> = {};
    if (businessId.trim()) roots['BUSINESS'] = { rootNodeId: businessId.trim() };
    if (functionalId.trim()) roots['FUNCTIONAL'] = { rootNodeId: functionalId.trim() };
    if (geographicId.trim()) roots['GEOGRAPHIC'] = { rootNodeId: geographicId.trim() };
    const payload = Object.keys(roots).length === 0 ? null : roots;
    try {
      setSaving(true);
      await pePortfolioService.updateScopeDataRoots(scope.id, payload);
      toast.success(successToast);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(pePortfolioService.errMessage(e));
    } finally {
      setSaving(false);
    }
  };

  if (!open || !scope) {
    return null;
  }

  const renderSelect = (
    type: (typeof TYPES)[number],
    value: string,
    setValue: (v: string) => void
  ) => {
    const list = nodes?.[type] ?? [];
    return (
      <label className="block">
        <span className="text-xs font-medium text-[var(--color-secondary-text)]">{labelForType(type)}</span>
        <select
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={loading || saving}
          className="mt-1 w-full rounded-md border border-[var(--color-border)] px-3 py-2 text-sm bg-[var(--color-surface-bg)]"
        >
          <option value="">{fullTreeLabel}</option>
          {list.map((n) => (
            <option key={n.id} value={n.id}>
              {indentLabel(n)}
            </option>
          ))}
        </select>
      </label>
    );
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg border border-[var(--color-border)] bg-[var(--color-secondary-bg)] shadow-xl p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-[var(--color-surface-text)]">{title}</h2>
        <p className="text-sm text-[var(--color-secondary-text)]">{intro}</p>
        <p className="text-xs text-[var(--color-secondary-text)]">
          {scope.child_tenant_name?.trim() || scope.child_tenant_id}
        </p>
        {loading ? (
          <p className="text-sm text-[var(--color-secondary-text)]">…</p>
        ) : (
          <div className="space-y-3">
            {renderSelect('BUSINESS', businessId, setBusinessId)}
            {renderSelect('FUNCTIONAL', functionalId, setFunctionalId)}
            {renderSelect('GEOGRAPHIC', geographicId, setGeographicId)}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            disabled={saving}
            onClick={onClose}
            className="rounded-md px-4 py-2 text-sm border border-[var(--color-border)] text-[var(--color-surface-text)]"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            disabled={loading || saving}
            onClick={() => void onSave()}
            className="rounded-md px-4 py-2 text-sm font-medium bg-[var(--color-primary-bg)] text-[var(--color-primary-text)]"
          >
            {saveLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
